import {DomainStore, fetchModels} from 'shared/store';
import { makeAutoObservable } from 'mobx';
import {successAlert} from 'shared/tools';
import {setupAutosaveDraft} from 'main/tools';
import {t} from 'shared/core';
import {endpoints, types} from 'main/tools';
import {Goal, GoalComment, Metric} from 'main/stores/performance_goals';
import _ from 'lodash';

class ViewGoalContainerState {
  store = new DomainStore();

  goal;
  comment;
  errors = {};
  deleteCommentModalOpen = false;
  isEditingComment = false;
  editingComment;
  editingCommentChanged = false;
  duplicateAssignees = [];
  selectedGoal = {};

  constructor() {
    makeAutoObservable(this, {
      store: false
    });
  }

  receiveProps({match, history}) {
    this.match = match;
    this.history = history;
  }

  async load() {
    await this.loadGoal();
  }

  async loadGoal() {
    this.store.invalidate(types.PERFORMANCE_GOALS.GOAL);

    await this.store._compose([
      endpoints.PERFORMANCE_GOALS.GOAL.with(this.match.params.goalId)
    ]);

    this.goal = new Goal(this.store._getSingle(
      types.PERFORMANCE_GOALS.GOAL,
      {id: this.match.params.goalId}
    ));
    await this.initializeComment();
    await this.fetchAvailableParentGoals();
  }

  async initializeComment() {
    this.comment = new GoalComment({
      goalId: this.goal.id,
      text: ''
    });
    await setupAutosaveDraft(
      this.comment,
      {id: this.goal.id, type: types.PERFORMANCE_GOALS.TIMELINE.COMMENT}
    );
  }

  async deleteGoal() {
    await this.store.destroy(this.goal);
    this.closeDeleteGoalModal();
    successAlert(t('goals.Goal deleted'));
    this.onBackClick();
  }

  editComment(comment) {
    this.editingComment = new GoalComment(comment);
    this.isEditingComment = true;
  }

  cancelEditingComment() {
    this.editingComment = null;
    this.editingCommentChanged = false;
    this.isEditingComment = false;
  }

  toggleEditingMetric(metricViewModel) {
    if (metricViewModel.isEditing) {
      this.cancelEditingMetric(metricViewModel);
    } else {
      this.goal.sortedMetricViewModels.forEach(metricViewModel => this.cancelEditingMetric(metricViewModel));
      metricViewModel.editingValue = metricViewModel.data.currentValue;
      metricViewModel.isEditing = true;
    }
  }

  cancelEditingMetric(metricViewModel) {
    metricViewModel.isEditing = false;
  }

  async saveComment() {
    const {model, errors} = await this.store.patch(this.editingComment);

    this.errors = errors;

    if (model) {
      const existingComment = _.find(this.goal.goalTimelineEntries, {id: this.editingComment.id});
      existingComment.text = this.editingComment.text;
      this.cancelEditingComment();
    }
  }

  async saveMetric(metric) {
    let editedMetric = new Metric(metric.data);
    editedMetric.currentValue = metric.editingValue;

    const {model, errors} = await this.store.post(
      metric.data.link('updateProgress'),
      types.PERFORMANCE_GOALS.METRIC,
      editedMetric,
    );

    this.errors = errors;

    if (model) {
      await this.loadGoal();
    }
  }

  updateComment(text) {
    this.comment.text = text;
    this.comment.autosaver.autosave();
  }

  updateEditingComment(text) {
    this.editingComment.text = text;
    this.editingCommentChanged = true;
  }

  async createComment() {
    const {model, errors} = await this.store.post(
      this.goal.link('createComment'),
      types.PERFORMANCE_GOALS.TIMELINE.COMMENT,
      this.comment
    );
    this.errors = errors;

    if (model) {
      this.addCommentToGoal(model);
      await this.initializeComment();
    }
  }

  addCommentToGoal(model) {
    const comment = new GoalComment(model);
    this.goal.goalTimelineEntries.unshift(comment);
  }

  openDeleteCommentModal(comment) {
    this.deleteCommentModalOpen = true;
    this.cancelEditingComment();
    this.editingComment = comment;
  }

  closeDeleteCommentModal() {
    this.deleteCommentModalOpen = false;
    this.editingComment = null;
  }

  async goToGoal(goal) {
    this.history.push(`/view/${goal.id}`);
  }

  updateDuplicatingGoal(attributes) {
    this.selectedGoal.merge(attributes);
  }

  async fetchAvailableParentGoals() {
    const parentGoals = await fetchModels(
      endpoints.PERFORMANCE_GOALS.PARENT_GOALS_FOR_GOAL.with(this.goal.id),
      types.PERFORMANCE_GOALS.GOAL
    );
    this.availableParentGoals = parentGoals.map(model => new Goal(model));
  }

  changeDuplicateAssignees(employees) {
    this.duplicateAssignees = employees;
  }

  async duplicateSelectedGoal() {
    const {model, errors} = await this.store.post(
      endpoints.PERFORMANCE_GOALS.GOAL.DUPLICATE.with(this.selectedGoal.id),
      types.PERFORMANCE_GOALS.GOAL,
      {
        ..._.pick(this.selectedGoal, ['startDate', 'endDate', 'parentGoal']),
        assignees: this.duplicateAssignees
      }
    );

    this.errors = errors;
    if (model) {
      this.closeDuplicateGoalModal();
      this.history.push('/');
      successAlert(t('goals.Goal duplicated'));
    }
  }

  async goToEditGoal() {
    this.history.push({
      pathname: `/edit/${this.goal.id}`,
      state: {
        fromViewPage: true
      }
    });
  }

  async deleteComment() {
    const comment = this.editingComment;
    await this.store.destroy(this.editingComment);
    this.closeDeleteCommentModal();
    successAlert(t('goals.Comment deleted'));
    this.goal.goalTimelineEntries.remove(comment);
  }

  get changesMade() {
    return this.comment.text !== '';
  }

  get canEdit() {
    return this.goal.canPatch;
  }

  onBackClick() {
    const fromEditPage = _.get(this.history.location.state, 'fromEditPage');
    const fromViewPage = _.get(this.history.location.state, 'fromViewPage');

    if (fromEditPage && fromViewPage) {
      this.history.go(-3);
    } else if (fromEditPage) {
      this.history.go(-2);
    } else {
      this.history.goBack();
    }
  }
}

export default ViewGoalContainerState;
